import api from './api';
import logger from '../utils/logger';

/**
 * DeltaSyncManager - Handles efficient delta sync with backend
 * Only fetches changes since last sync, reducing data transfer by 90%+
 */
class DeltaSyncManager {
  constructor() {
    this.clientId = null;
    this.syncTimestamps = {};
    this.pendingSyncs = new Set();
    this.initialized = false;
    
    // Auto-initialize when instantiated
    this.initialize();
  }

  /**
   * Initialize the sync manager
   */
  async initialize() {
    if (this.initialized) return;

    try {
      this.clientId = await this.getOrCreateClientId();
      this.syncTimestamps = await this.loadSyncTimestamps();
      this.initialized = true;
      
      logger.info('[DeltaSync] Initialized', {
        clientId: this.clientId,
        syncStates: Object.keys(this.syncTimestamps)
      });
    } catch (error) {
      logger.error('[DeltaSync] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Get or create a unique client ID
   */
  async getOrCreateClientId() {
    try {
      let clientId = localStorage.getItem('delta_sync_client_id');
      
      if (!clientId) {
        clientId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('delta_sync_client_id', clientId);
        logger.info('[DeltaSync] Created new client ID:', clientId);
      }
      
      return clientId;
    } catch (error) {
      logger.error('[DeltaSync] Failed to get/create client ID:', error);
      return `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  /**
   * Perform delta sync for entity type
   * @param {string} entityType - 'product', 'category', 'banner'
   * @returns {Promise<Object>} Delta changes
   */
  async deltaSync(entityType) {
    // Ensure initialized
    if (!this.initialized) {
      await this.initialize();
    }

    // Check for pending sync
    if (this.pendingSyncs.has(entityType)) {
      logger.debug(`[DeltaSync] Sync already in progress for ${entityType}`);
      return null;
    }

    this.pendingSyncs.add(entityType);

    try {
      const lastSync = this.syncTimestamps[entityType] || 0;
      
      logger.debug(`[DeltaSync] Starting delta sync for ${entityType}`, {
        lastSync: new Date(lastSync).toISOString(),
        clientId: this.clientId
      });

      const response = await api.post('/v2/sync/delta', {
        entity_type: entityType,
        since: lastSync,
        client_id: this.clientId,
        limit: 1000
      });

      const data = response.data; // axios returns response.data

      if (data.success) {
        // Update local sync timestamp
        this.syncTimestamps[entityType] = data.sync_timestamp;
        await this.saveSyncTimestamps();

        logger.info(`[DeltaSync] Delta sync completed for ${entityType}`, {
          created: data.changes.created.length,
          updated: data.changes.updated.length,
          deleted: data.changes.deleted.length,
          hasMore: data.has_more
        });

        return {
          created: data.changes.created || [],
          updated: data.changes.updated || [],
          deleted: data.changes.deleted || [],
          hasMore: data.has_more || false,
          isDelta: true
        };
      } else {
        throw new Error(data.message || 'Delta sync failed');
      }

    } catch (error) {
      logger.error(`[DeltaSync] Delta sync error for ${entityType}:`, error);
      
      // Handle rate limiting (429 Too Many Requests)
      if (error.response?.status === 429) {
        const retryAfter = parseInt(error.response.headers['retry-after'] || '60');
        logger.warn(`[DeltaSync] Rate limited. Retrying after ${retryAfter}s`);
        
        // Wait and retry delta sync
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        this.pendingSyncs.delete(entityType);
        return await this.deltaSync(entityType);
      }
      
      // Fallback to full sync on other errors
      logger.warn(`[DeltaSync] Falling back to full sync for ${entityType}`);
      return await this.fullSync(entityType);
      
    } finally {
      this.pendingSyncs.delete(entityType);
    }
  }

  /**
   * Perform full sync (fallback or initial sync)
   * @param {string} entityType
   * @returns {Promise<Object>}
   */
  async fullSync(entityType) {
    logger.info(`[DeltaSync] Performing full sync for ${entityType}`);
    
    try {
      let response;
      
      switch (entityType) {
        case 'product':
          response = await api.get('/v2/products');
          break;
        case 'category':
          response = await api.get('/v2/categories');
          break;
        case 'banner':
          response = await api.get('/v2/banners');
          break;
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }

      // Extract data from axios response
      const responseData = response.data; // axios wraps in response.data
      const data = responseData.data || responseData.categories || responseData;
      const syncTimestamp = responseData.sync_timestamp || Date.now();

      // Update sync timestamp
      this.syncTimestamps[entityType] = syncTimestamp;
      await this.saveSyncTimestamps();

      logger.info(`[DeltaSync] Full sync completed for ${entityType}`, {
        count: Array.isArray(data) ? data.length : 0
      });

      return {
        created: Array.isArray(data) ? data : [],
        updated: [],
        deleted: [],
        hasMore: false,
        isFullSync: true
      };

    } catch (error) {
      logger.error(`[DeltaSync] Full sync error for ${entityType}:`, error);
      throw error;
    }
  }

  /**
   * Check sync status for entity type
   * @param {string} entityType
   * @returns {Promise<Object>}
   */
  async getSyncStatus(entityType) {
    try {
      const response = await api.get('/v2/sync/status', {
        params: {
          client_id: this.clientId,
          entity_type: entityType
        }
      });

      return response.data;
    } catch (error) {
      logger.error('[DeltaSync] Failed to get sync status:', error);
      return null;
    }
  }

  /**
   * Reset sync state (force full sync next time)
   * @param {string|null} entityType - Specific type or null for all
   */
  async resetSync(entityType = null) {
    try {
      if (entityType) {
        delete this.syncTimestamps[entityType];
        logger.info(`[DeltaSync] Reset sync state for ${entityType}`);
      } else {
        this.syncTimestamps = {};
        logger.info('[DeltaSync] Reset all sync states');
      }
      
      await this.saveSyncTimestamps();

      // Also notify backend
      await api.post('/v2/sync/reset', {
        client_id: this.clientId,
        entity_type: entityType
      });

    } catch (error) {
      logger.error('[DeltaSync] Failed to reset sync:', error);
    }
  }

  /**
   * Load sync timestamps from localStorage
   */
  async loadSyncTimestamps() {
    try {
      const stored = localStorage.getItem('delta_sync_timestamps');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      logger.error('[DeltaSync] Failed to load sync timestamps:', error);
      return {};
    }
  }

  /**
   * Save sync timestamps to localStorage
   */
  async saveSyncTimestamps() {
    try {
      localStorage.setItem('delta_sync_timestamps', JSON.stringify(this.syncTimestamps));
    } catch (error) {
      logger.error('[DeltaSync] Failed to save sync timestamps:', error);
    }
  }

  /**
   * Get last sync timestamp for entity type
   * @param {string} entityType
   * @returns {number|null}
   */
  getLastSyncTime(entityType) {
    return this.syncTimestamps[entityType] || null;
  }

  /**
   * Check if entity type has been synced before
   * @param {string} entityType
   * @returns {boolean}
   */
  hasSynced(entityType) {
    return !!this.syncTimestamps[entityType];
  }

  /**
   * Get sync statistics
   * @returns {Object}
   */
  getStats() {
    return {
      clientId: this.clientId,
      initialized: this.initialized,
      syncedEntityTypes: Object.keys(this.syncTimestamps),
      pendingSyncs: Array.from(this.pendingSyncs),
      lastSyncTimes: { ...this.syncTimestamps }
    };
  }

  /**
   * Clear all client-side delta sync data
   */
  async clearAll() {
    try {
      localStorage.removeItem('delta_sync_client_id');
      localStorage.removeItem('delta_sync_timestamps');
      this.clientId = null;
      this.syncTimestamps = {};
      this.pendingSyncs.clear();
      this.initialized = false;
      
      logger.info('[DeltaSync] Cleared all delta sync data');
    } catch (error) {
      logger.error('[DeltaSync] Failed to clear delta sync data:', error);
    }
  }
}

// Export singleton instance
export default new DeltaSyncManager();
